'use client';
import { ReactNode, useId, useState } from 'react';
import cn from 'classnames';
import styles from './video.module.scss';
import { IconVideoPlayButton } from '@/assets/icons/IconVideoPlayButton';
import { Modal } from '../../Modal/Modal';
import { VideoModal } from './VideoModal';

export interface StandardVideoProps {
    poster?: string;
    source?: string;
    altText?: string;
    className?: string;
    title?: string;
    description?: ReactNode;
    videoId?: string;
    handlePlayClick?: () => void;
    onFocus?: () => void;
    absoluteCentered?: boolean;
}

export function StandardVideo(props: StandardVideoProps): React.JSX.Element {
    const generatedId = useId();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const thumbnailUrl = `https://img.youtube.com/vi/${props.videoId}/hqdefault.jpg`;
    const videoContainerClassNames = cn(styles.videoContainer, props.className, {
        [styles.absoluteCentered]: props.absoluteCentered,
    });

    const accessibleLabel = `Play video: ${props.altText || ''}`;

    const handleButtonClick = () => {
        if (props.handlePlayClick) {
            props.handlePlayClick();
        } else {
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            {!props.handlePlayClick && (
                <Modal
                    maxWidth={1024}
                    backgroundTransparent={true}
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    isOverflowHidden={true}
                    variant="video"
                    dataLocalpagename={props.videoId ? 'overlay:youtubevideo' : 'overlay:hostedvideo'}
                    keepContentsMounted={!!props.videoId}
                >
                    <VideoModal {...props} isModalOpen={isModalOpen} />
                </Modal>
            )}
            <div className={videoContainerClassNames}>
                <button className={styles.buttonWrapper} aria-label={accessibleLabel} onClick={handleButtonClick}>
                    {props?.videoId ? (
                        <img
                            src={props.poster || thumbnailUrl}
                            alt={props.altText || 'Video thumbnail'}
                            loading="lazy"
                        />
                    ) : (
                        <video muted poster={props.poster} aria-describedby={generatedId} preload="none">
                            <source src={props.source} type="video/mp4" />
                        </video>
                    )}
                    <div className={styles.playIcon}>
                        <IconVideoPlayButton />
                    </div>
                </button>
            </div>
        </>
    );
}
